import { LitElement, html, css } from 'lit';

import { baseStyles } from '../../../../neb-styles/neb-styles';
import {
  CSS_COLOR_WHITE,
  CSS_COLOR_BLACK,
  CSS_COLOR_HIGHLIGHT,
  CSS_FONT_SIZE_BODY,
  CSS_SPACING,
} from '../../../../neb-styles/neb-variables';

import '../../../../neb-styles/neb-icons';
import '../../../../../src/components/misc/neb-icon';
import '../../../../neb-lit-components/src/components/neb-popover';
import './neb-user-nav';

export const ELEMENTS = {
  welcomeLabel: { id: 'welcome-label' },
  loginLabel: { id: 'login-label' },
  menuIcon: { id: 'icon-menu' },
  userIcon: { id: 'icon-user' },
  userNav: { id: 'user-nav' },
  userNavPopover: { id: 'popover-user-nav' },
};

class NebUser extends LitElement {
  static get properties() {
    return {
      __showUserNav: Boolean,

      layout: {
        type: String,
        reflect: true,
      },
      userFirstName: String,
      showMyPayments: Boolean,
    };
  }

  static get styles() {
    return [
      baseStyles,
      css`
        :host {
          display: flex;
          flex-direction: column;
          padding-right: 53px;
          justify-content: space-around;
        }
        .user-container {
          position: relative;
          width: fit-content;
          width: -moz-fit-content;
          display: flex;
        }

        .user-icon {
          fill: ${CSS_COLOR_HIGHLIGHT};
          height: 35px;
          width: 35px;
          align-self: center;
          cursor: pointer;
          position: relative;
        }

        .arrow-icon {
          height: 22px;
          width: 12px;
          margin-left: 10px;
        }

        .user-label-container {
          display: flex;
          flex-direction: column;
          align-self: center;
          cursor: pointer;
        }

        .welcome-container {
          display: flex;
          padding-right: 5px;
        }

        .welcome-label {
          font-size: ${CSS_FONT_SIZE_BODY};
          margin-left: ${CSS_SPACING};
        }

        :host(:not([layout='small'])) .login-container {
          padding: 0;
          margin: 0;
        }

        :host([__showusernav]) .arrow-icon {
          transform: scaleY(-1);
        }

        :host([__showusernav]) .welcome-container {
          color: var(--neb-font-color-highlight);
        }

        .login-label {
          justify-content: flex-start;
          padding-right: 5px;
          font-size: ${CSS_FONT_SIZE_BODY};
          color: ${CSS_COLOR_HIGHLIGHT};
          margin-left: ${CSS_SPACING};
        }

        :host([layout='small']) .login-label {
          display: none;
        }

        :host([layout='small']) {
          padding-right: ${CSS_SPACING};
        }

        :host([layout='small']) .user-container {
          justify-content: flex-end;
          margin-top: ${CSS_SPACING};
          width: unset;
          position: unset;
        }

        :host(:not([layout='small'])) .login-container {
          margin: ${CSS_SPACING};
        }

        .login-container {
          min-width: 300px;
        }

        .popover {
          position: absolute;
          top: 100%;
          z-index: 2;
        }

        .container-popover {
          display: flex;
          justify-content: center;
          height: 100%;
          width: 100%;
          position: absolute;
        }

        :host([layout='small']) .mobile {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: ${CSS_COLOR_WHITE};
          z-index: 2;
          color: ${CSS_COLOR_BLACK};
        }
      `,
    ];
  }

  constructor() {
    super();

    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.__showUserNav = false;

    this.layout = '';
    this.userFirstName = '';
    this.showMyPayments = false;

    this.onNavigateToMyAppointments = () => {};

    this.onResetOnLogout = () => {};

    this.onNavigateToLogin = () => {};

    this.onNavigateToMyPayments = () => {};
  }

  __initHandlers() {
    this.__handlers = {
      click: () => {
        if (this.userFirstName) {
          this.__showUserNav = !this.__showUserNav;
        } else {
          this.onNavigateToLogin();
        }
      },
      performCreateAccount: () => {
        this.__handlers.closeLogin();

        this.dispatchEvent(
          new CustomEvent('neb-create-account-user-menu', {
            bubbles: true,
            composed: true,
          }),
        );
      },
      logout: () => {
        this.onResetOnLogout();

        this.__handlers.closeUserNav();
      },
      closeUserNav: () => {
        this.__showUserNav = false;
      },
      navigateToAppointments: () => {
        this.onNavigateToMyAppointments();

        this.__handlers.closeUserNav();
      },
      navigateToPayments: () => {
        this.onNavigateToMyPayments();

        this.__handlers.closeUserNav();
      },
    };
  }

  _computeWelcomeLabel() {
    return this.userFirstName
      ? `Welcome ${this.userFirstName}`
      : 'Returning User?';
  }

  _computeLoginLabel() {
    return this.userFirstName ? '' : 'Login';
  }

  __renderUserNav() {
    return html`
      <neb-user-nav
        id="${ELEMENTS.userNav.id}"
        class="user-nav mobile"
        .layout="${this.layout}"
        .title="${this._computeWelcomeLabel()}"
        .onNavigateToMyAppointments="${this.__handlers.navigateToAppointments}"
        .onNavigateToMyPayments="${this.__handlers.navigateToPayments}"
        .onClose="${this.__handlers.closeUserNav}"
        .onLogout="${this.__handlers.logout}"
        .showMyPayments="${this.showMyPayments}"
      ></neb-user-nav>
    `;
  }

  __renderUserMenu() {
    return this.layout === 'small'
      ? this.__renderUserNav()
      : html`
          <div class="container-popover">
            <neb-popover
              id="${ELEMENTS.userNavPopover.id}"
              ?layout-small="${this.layout === 'small'}"
              class="popover"
              .onBlockerClicked="${this.__handlers.closeUserNav}"
            >
              ${this.__renderUserNav()}
            </neb-popover>
          </div>
        `;
  }

  render() {
    return html`
      <div class="user-container">
        <neb-icon
          id="${ELEMENTS.userIcon.id}"
          class="user-icon"
          icon="neb:accountCircle"
          @click="${this.__handlers.click}"
        ></neb-icon>

        <div class="user-label-container" @tap="${this.__handlers.click}">
          <div class="welcome-container">
            ${this.layout !== 'small' || this.userFirstName
              ? html`
                  <div id="${ELEMENTS.welcomeLabel.id}" class="welcome-label">
                    ${this._computeWelcomeLabel(this.userFirstName)}
                  </div>
                `
              : ''}
            ${this.userFirstName && this.layout !== 'small'
              ? html`
                  <iron-icon
                    id="${ELEMENTS.menuIcon.id}"
                    class="arrow-icon"
                    icon="neb:down"
                  ></iron-icon>
                `
              : ''}
          </div>

          <div id="${ELEMENTS.loginLabel.id}" class="login-label">
            ${this._computeLoginLabel(this.userFirstName)}
          </div>
        </div>

        ${this.__showUserNav ? this.__renderUserMenu() : ''}
      </div>
    `;
  }
}

customElements.define('neb-user', NebUser);
